import { useEffect, useState } from "react";
import { backendbaseUrl } from "../../Utils/baseUrl";
import axiosInstance from "../../Utils/axiosInstance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { encryptData } from "../../Tools/Cipher/EncryptData";
import { Container, Grid, Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

const ProjectSelection = () => {
  const navigate = useNavigate();
  const [projectData, SetProjectData] = useState<any>([]);
  const [username, setUsername] = useState("");

  useEffect(() => {
    const getApps = async () => {
      try {
        const url = `${backendbaseUrl}/company/subscribed-apps/list/`;
        const response = await axiosInstance.get(url);
        if (response.status === 200) {
          console.log("Fetched Apps:", response.data);
          SetProjectData(response.data);
        }
      } catch (error: any) {
        handleError(error);
      }
    };
    getApps();
    axiosInstance
      .get("/users/user-details/")
      .then((res) => {
        const user = res.data?.data;
        if (user?.name) {
          setUsername(user.name);
        }
      })
      .catch((err) => {
        console.error("Error fetching user details:", err);
        setUsername("Guest"); // fallback
      });
  }, []);

  const handleError = (error: any) => {
    if (error.response) {
      if (error.response.status === 401) {
        toast.error("Unauthorized Access");
      } else {
        toast.error(error.response.data.message || "An unknown error occurred");
      }
    } else {
      toast.error("Network error or server is down");
    }
  };

  const goToChat = () => {
    navigate("/applications/chat/chat_private");
  };

  const handleClick = async (appName: string) => {
    console.log("Selected App:", appName);

    try {
      const url = `${backendbaseUrl}/company/subscribed-apps/resource/?app_name=${appName}`;
      const response = await axiosInstance.get(url);
      if (response.status === 200) {
        console.log("Fetched Permissions:", response.data.data);

        // Encrypt data before saving
        const encryptedData = encryptData(response.data.data);
        if (encryptedData) {
          localStorage.setItem("resourceList", encryptedData);
        } else {
          console.error("Failed to encrypt data.");
        }

        SetProjectData(response.data.data);
        navigate("/company/verify");
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #021526 0%, #002b3f 50%, #009db5 120%)",
        color: "#fff",
        py: 6,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle glowing background shapes */}
      <Box
        sx={{
          position: "absolute",
          width: "400px",
          height: "400px",
          top: "-100px",
          right: "-100px",
          borderRadius: "50%",
          background: "rgba(0,157,181,0.25)",
          filter: "blur(120px)",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "350px",
          height: "350px",
          bottom: "-100px",
          left: "-100px",
          borderRadius: "50%",
          background: "rgba(0,157,181,0.15)",
          filter: "blur(120px)",
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2, mt: 2 }}>
        {/* 👋 Compact Welcome Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 4,
            px: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: "1rem",
              letterSpacing: "0.4px",
              color: "#bde9f2",
              textShadow: "0 0 6px rgba(0,157,181,0.4)",
            }}
          >
            Welcome back,{" "}
            <Box component="span" sx={{ color: "#00c4e4", fontWeight: 600 }}>
              {username}
            </Box>
          </Typography>

          <Typography
            sx={{
              fontSize: "0.85rem",
              color: "#82cfd9",
              opacity: 0.9,
            }}
          >
            {new Date().toLocaleDateString("en-GB", {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </Typography>
        </Box>

        {/* 🔹 Project Cards */}
        <Grid container spacing={2.5} justifyContent="center">
          {projectData.map((data: any, index: number) => {
            const app = data.subscribed_apps?.apps;
            if (!app) return null;

            return (
              <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                <motion.div
                  whileHover={{ scale: 1.06, y: -3 }}
                  transition={{ type: "spring", stiffness: 250 }}
                >
                  <Box
                    onClick={() => handleClick(app.name)}
                    sx={{
                      cursor: "pointer",
                      textAlign: "center",
                      p: 2.2,
                      borderRadius: "14px",
                      backdropFilter: "blur(8px)",
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "rgba(255,255,255,0.12)",
                        boxShadow: "0 6px 18px rgba(0,157,181,0.4)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={app.logo}
                      alt={app.name}
                      sx={{
                        width: 50,
                        height: 50,
                        mb: 1.2,
                        borderRadius: "10px",
                        objectFit: "contain",
                        background: "rgba(0,157,181,0.08)",
                        p: 1.2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        letterSpacing: "0.3px",
                        fontSize: "0.88rem",
                        color: "#e6f7fa",
                      }}
                    >
                      {app.name}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>

        {/* 🔹 Bottom Utility Cards */}
        <Grid
          container
          spacing={2.5}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 4 }}
        >
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <motion.div whileHover={{ scale: 1.06, y: -3 }}>
              <Box
                sx={{
                  cursor: "pointer",
                  textAlign: "center",
                  p: 2.2,
                  borderRadius: "14px",
                  backdropFilter: "blur(8px)",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  "&:hover": {
                    background: "rgba(255,255,255,0.12)",
                    boxShadow: "0 6px 18px rgba(0,157,181,0.4)",
                  },
                }}
              >
                <Box
                  component="img"
                  src="https://cdn-icons-gif.flaticon.com/12707/12707772.gif"
                  alt="Acers Store"
                  sx={{
                    width: 50,
                    height: 50,
                    mb: 1.2,
                    borderRadius: "10px",
                    objectFit: "contain",
                    background: "rgba(0,157,181,0.08)",
                    p: 1.2,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    letterSpacing: "0.3px",
                    fontSize: "0.88rem",
                    color: "#e6f7fa",
                  }}
                >
                  Acers Store
                </Typography>
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={6} sm={4} md={3} lg={2}>
            <motion.div whileHover={{ scale: 1.06, y: -3 }}>
              <Box
                onClick={goToChat}
                sx={{
                  cursor: "pointer",
                  textAlign: "center",
                  p: 2.2,
                  borderRadius: "14px",
                  backdropFilter: "blur(8px)",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  "&:hover": {
                    background: "rgba(255,255,255,0.12)",
                    boxShadow: "0 6px 18px rgba(0,157,181,0.4)",
                  },
                }}
              >
                <Box
                  component="img"
                  src="https://ugokawaii.com/wp-content/uploads/2023/06/conversation.gif"
                  alt="Chat"
                  sx={{
                    width: 50,
                    height: 50,
                    mb: 1.2,
                    borderRadius: "10px",
                    objectFit: "contain",
                    background: "rgba(0,157,181,0.08)",
                    p: 1.2,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    letterSpacing: "0.3px",
                    fontSize: "0.88rem",
                    color: "#e6f7fa",
                  }}
                >
                  Chat
                </Typography>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProjectSelection;
